import React, { useMemo, useState } from 'react';
import { advanceAppointment, getCallRecordingUrl, type RdvRow } from '../../lib/refonteApi';
import { NqReasonForm } from './NqReasonPicker';

/**
 * Pop-up client OBLIGATOIRE (hook-brain : le client fixe qualifié/non qualifié) :
 * au login, si des RDV passés n'ont pas leur retour, on demande au client de les
 * traiter — a-t-il eu lieu ? était-il qualifié ? — avant d'accéder au reste.
 * But : ne rater aucun RDV, maximiser les retours clients. Escalade, pas voie par défaut :
 * un lien discret « Plus tard » évite de piéger le client.
 */
const ClientRdvGate: React.FC<{ queue: RdvRow[]; onClose: () => void; onChanged: () => void }> = ({ queue, onClose, onChanged }) => {
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  // Écouter l'appel AVANT de répondre « eu lieu ? / qualifié ? » : meilleure donnée contractuelle,
  // meilleure expérience (audit UX 09/10 #8). Chargé à la demande (URL signée).
  const [audio, setAudio] = useState<{ id: string; url: string } | null>(null);
  const [audioBusy, setAudioBusy] = useState(false);

  const rdv = queue[idx];
  const total = queue.length;
  // Étape : si déjà honoré → directement la qualif ; sinon on demande d'abord la présence.
  const needPresence = rdv && (rdv.lifecycle_status === 'posed' || rdv.lifecycle_status === 'confirmed');
  const [step, setStep] = useState<'presence' | 'qualif' | 'nq_reason'>(needPresence ? 'presence' : 'qualif');

  const next = () => {
    setAudio(null);
    setDone(d => d + 1);
    if (idx + 1 >= total) { onChanged(); onClose(); return; }
    const n = queue[idx + 1];
    setStep(n.lifecycle_status === 'posed' || n.lifecycle_status === 'confirmed' ? 'presence' : 'qualif');
    setIdx(idx + 1);
  };

  const act = async (to: 'held' | 'no_show' | 'qualified' | 'not_qualified') => {
    if (!rdv) return;
    setBusy(true); setErr(null);
    try {
      // « non qualifié » exige un motif en LISTE FERMÉE (CHECK en base) → étape dédiée,
      // plus de window.prompt texte libre qui faisait échouer l'enregistrement (audit 08/10).
      if (to === 'not_qualified') { setStep('nq_reason'); setBusy(false); return; }
      await advanceAppointment(rdv.id, to, { icpConformant: to === 'qualified' ? true : undefined });
      if (to === 'held') { setStep('qualif'); }   // il a eu lieu → on enchaîne sur la qualif
      else { next(); }                             // no-show / qualifié / non qualifié → RDV suivant
    } catch (e: any) { setErr(e.message || 'Échec.'); }
    setBusy(false);
  };

  const submitNq = async (reason: string, note?: string) => {
    if (!rdv) return;
    setBusy(true); setErr(null);
    try { await advanceAppointment(rdv.id, 'not_qualified', { reason, note }); next(); }
    catch (e: any) { setErr(e.message || 'Échec.'); }
    setBusy(false);
  };

  const remaining = useMemo(() => total - done, [total, done]);
  if (!rdv) return null;

  const Btn: React.FC<{ onClick: () => void; tone?: 'ok' | 'bad' | 'neutral'; children: React.ReactNode }> = ({ onClick, tone = 'neutral', children }) => (
    <button disabled={busy} onClick={onClick}
      className={`flex-1 text-[13px] font-semibold px-4 py-3 rounded-lg border disabled:opacity-40 transition
        ${tone === 'ok' ? 'border-st-ok/40 text-st-ok hover:bg-st-ok/5'
          : tone === 'bad' ? 'border-st-bad/40 text-st-bad hover:bg-st-bad/5'
          : 'border-line-2 text-dim hover:border-ink'}`}>{children}</button>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-panel border border-line rounded-card w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-baseline justify-between mb-1">
          <span className="font-mono text-[9px] uppercase tracking-widest text-hook-rose">Retour rendez-vous · {done + 1}/{total}</span>
          <span className="font-mono text-[10px] text-faint">{remaining} restant{remaining > 1 ? 's' : ''}</span>
        </div>
        <h3 className="text-[17px] font-bold tracking-tight mb-0.5">{rdv.prospect_name || 'Prospect'}</h3>
        <p className="text-dim text-[13px] mb-1.5">
          {rdv.company_name && <>{rdv.company_name} · </>}{rdv.date ? new Date(rdv.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'sans date'}
        </p>
        <p className="text-faint text-[11.5px] mb-3">Vos retours valident nos rendez-vous — c'est ce qui garantit un suivi et une facturation justes. 30 secondes.</p>
        {(rdv as any).call_id && (
          <div className="mb-3">
            {audio?.id === rdv.id
              ? <audio controls autoPlay src={audio.url} className="w-full h-9" />
              : (
                <button disabled={audioBusy} onClick={async () => {
                  setAudioBusy(true);
                  const u = await getCallRecordingUrl((rdv as any).call_id).catch(() => null);
                  if (u) setAudio({ id: rdv.id, url: u });
                  setAudioBusy(false);
                }} className="text-[12px] font-semibold text-hook-rose hover:underline disabled:opacity-40">
                  {audioBusy ? 'Chargement…' : "🎧 Réécouter l'appel qui a donné ce rendez-vous"}
                </button>
              )}
          </div>
        )}

        {err && <p className="text-st-bad text-[12px] mb-3">{err}</p>}

        {step === 'nq_reason' ? (
          <NqReasonForm busy={busy} onSubmit={submitNq} onCancel={() => setStep('qualif')} />
        ) : step === 'presence' ? (
          <>
            <p className="text-[14px] font-semibold mb-3">Ce rendez-vous a-t-il eu lieu ?</p>
            <div className="flex gap-2">
              <Btn tone="ok" onClick={() => act('held')}>✓ Oui, il a eu lieu</Btn>
              <Btn tone="bad" onClick={() => act('no_show')}>Non (no-show)</Btn>
            </div>
          </>
        ) : (
          <>
            <p className="text-[14px] font-semibold mb-1">Était-il qualifié ?</p>
            <p className="text-dim text-[12px] mb-3">Qualifié = conforme à votre cible et à vos critères.</p>
            <div className="flex gap-2">
              <Btn tone="ok" onClick={() => act('qualified')}>✓ Qualifié</Btn>
              <Btn tone="bad" onClick={() => act('not_qualified')}>Non qualifié</Btn>
            </div>
          </>
        )}

        <button onClick={onClose} className="mt-4 w-full text-center text-[12.5px] px-4 py-2 rounded-lg border border-line-2 text-dim hover:border-ink transition">Remplir plus tard</button>
      </div>
    </div>
  );
};

export default ClientRdvGate;
